import { EditOutlined, ReloadOutlined, SyncOutlined, UndoOutlined } from "@ant-design/icons";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { Form, Modal, Popconfirm, Space, Typography, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BillingService } from "../../../api/billingService";
import { ExchangeRate } from "../../../@type/billing.types";
import { PERM } from "../../../auth/permissions";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import Card from "../../../components/common/Card";
import InputField from "../../../components/common/fields/InputField";
import MuiTable from "../../../components/common/MuiTable";
import StatusBadge from "../../../components/common/StatusBadge";
import { useMe } from "../../../auth/MeContext";
import { Column } from "../../../utils/constants";

const unwrap = (response: any) => response?.data?.data ?? response?.data ?? [];

const formatRate = (value: number) =>
  Number(value).toLocaleString("en-US", { maximumFractionDigits: 8 });

const ExchangeRateConfig = () => {
  const [form] = Form.useForm<{ rate: number }>();
  const [messageApi, contextHolder] = message.useMessage();
  const { can } = useMe();
  const canUpdate = can(PERM.EXCHANGE_RATE_UPDATE);
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ExchangeRate | null>(null);

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      setRates(unwrap(await BillingService.getExchangeRates()));
    } catch (error) {
      messageApi.error("Failed to load exchange rates");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const baseCurrency = rates[0]?.base_currency || "USD";

  const filtered = useMemo(() => {
    const keyword = search.trim().toUpperCase();
    return keyword ? rates.filter((rate) => rate.currency.includes(keyword)) : rates;
  }, [rates, search]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      setRates(unwrap(await BillingService.refreshExchangeRates()));
      messageApi.success("Exchange rates refreshed (manual overrides kept)");
    } catch (error) {
      messageApi.error("Failed to refresh exchange rates");
    } finally {
      setRefreshing(false);
    }
  };

  const openEdit = (rate: ExchangeRate) => {
    form.setFieldsValue({ rate: rate.rate });
    setEditing(rate);
  };

  const saveManual = async () => {
    if (!editing) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await BillingService.setManualExchangeRate(editing.currency, Number(values.rate));
      messageApi.success(`${editing.currency} rate overridden`);
      setEditing(null);
      await loadRates();
    } catch (error: any) {
      if (!error?.errorFields) {
        messageApi.error("Failed to save exchange rate");
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToAuto = async (rate: ExchangeRate) => {
    try {
      await BillingService.resetExchangeRate(rate.currency);
      messageApi.success(`${rate.currency} is back to automatic rate`);
      await loadRates();
    } catch (error) {
      messageApi.error("Failed to reset exchange rate");
    }
  };

  const header: Column[] = [
    {
      id: "currency",
      label: "Currency",
      minWidth: 100,
      align: "left",
      format: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      id: "rate",
      label: `Rate (per 1 ${baseCurrency})`,
      minWidth: 160,
      align: "right",
      format: (value: number) => formatRate(value),
    },
    {
      id: "source",
      label: "Source",
      minWidth: 110,
      align: "center",
      format: (value: string) => (
        <StatusBadge
          label={value === "manual" ? "Manual" : "Auto"}
          tone={value === "manual" ? "warning" : "success"}
        />
      ),
    },
    {
      id: "fetched_at",
      label: "Updated at",
      minWidth: 170,
      align: "center",
      format: (value: string) => (value ? value.replace("T", " ").slice(0, 19) : ""),
    },
    ...(canUpdate
      ? [
          {
            id: "actions",
            label: "Actions",
            minWidth: 110,
            align: "center",
            format: (_: unknown, row: ExchangeRate) => (
              <Space size={4}>
                <IconButton size="small" color="success" onClick={() => openEdit(row)}>
                  <EditOutlined />
                </IconButton>
                {row.source === "manual" && (
                  <Popconfirm
                    title="Use the automatic rate again?"
                    description="The override is removed and the latest provider rate is applied."
                    onConfirm={() => resetToAuto(row)}
                  >
                    <IconButton size="small" color="warning">
                      <UndoOutlined />
                    </IconButton>
                  </Popconfirm>
                )}
              </Space>
            ),
          } as Column,
        ]
      : []),
  ];

  return (
    <Card className="user">
      {contextHolder}
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Exchange Rates
            </Typography.Title>
            <Typography.Text type="secondary">
              Rates are fetched daily. A manual override stays until it is reset to automatic.
            </Typography.Text>
          </div>
          <Space>
            <InputField
              placeholder="Search currency (e.g. VND)"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              endAdornment={<InputAdornment position="end">⌕</InputAdornment>}
            />
            <CancelButton startIcon={<ReloadOutlined />} onClick={loadRates} loading={loading}>
              Reload
            </CancelButton>
            {canUpdate && (
              <ActionButton startIcon={<SyncOutlined />} onClick={refreshAll} loading={refreshing}>
                Fetch latest
              </ActionButton>
            )}
          </Space>
        </Box>

        <MuiTable header={header} data={filtered} isLoading={loading} />
      </Space>

      <Modal
        title={editing ? `Override ${editing.currency} rate` : ""}
        open={Boolean(editing)}
        onCancel={() => setEditing(null)}
        footer={
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <CancelButton onClick={() => setEditing(null)}>Cancel</CancelButton>
            <ActionButton onClick={saveManual} loading={saving}>
              Save
            </ActionButton>
          </Box>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rate"
            label={`1 ${baseCurrency} = ? ${editing?.currency ?? ""}`}
            rules={[
              { required: true, message: "Rate is required" },
              {
                validator: (_, value) =>
                  Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Rate must be greater than 0")),
              },
            ]}
          >
            <InputField
              type="number"
              inputProps={{ min: 0, step: "any" }}
              endAdornment={<InputAdornment position="end">{editing?.currency}</InputAdornment>}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ExchangeRateConfig;
